// Piezas de los formularios del catálogo con el lenguaje del mock: selección, interruptor, financieras y aviso de error.
import { useId, type ReactNode } from 'react';
import { Select, Switch } from '@/kit';
import { cn } from '@/lib/cn';
import { FINANCIER_OPTIONS } from '../../lib/catalogForms';

export {
    FormDrawer as EditorDrawer,
    FormDrawerFooter as EditorFooter,
    WriteErrorNotice,
} from '@/shared/patterns/CreateDrawer';

/** Radix no admite una opción con valor vacío: se traduce a un centinela. */
const EMPTY = '__empty__';

export const SECTION = 'text-[10.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

export function FormSelect({
    label,
    value,
    onChange,
    options,
    placeholder,
    error,
    required,
    disabled,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly { value: string; label: string }[];
    placeholder?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
}) {
    return (
        <Select
            label={label}
            value={value === '' && options.some((o) => o.value === '') ? EMPTY : value}
            onChange={(next) => {
                onChange(next === EMPTY ? '' : next);
            }}
            options={options.map((o) => ({
                value: o.value === '' ? EMPTY : o.value,
                label: o.label,
            }))}
            placeholder={placeholder}
            error={error}
            required={required}
            disabled={disabled}
        />
    );
}

export function FormSwitch({
    label,
    hint,
    checked,
    onChange,
    disabled,
}: {
    label: string;
    hint?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
            <div>
                <div className="text-[12px] font-semibold">{label}</div>
                {hint ? <div className="text-[10.5px] text-muted-foreground">{hint}</div> : null}
            </div>
            <Switch
                checked={checked}
                onCheckedChange={onChange}
                disabled={disabled}
                aria-label={label}
            />
        </div>
    );
}

/** Financieras elegibles en casillas, en orden alfabético. */
export function FinancierChecks({
    value,
    onChange,
}: {
    value: readonly string[];
    onChange: (next: string[]) => void;
}) {
    const id = useId();
    return (
        <fieldset>
            <legend className="mb-1 text-sm font-medium text-foreground">
                Financieras elegibles
            </legend>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {FINANCIER_OPTIONS.map((option) => {
                    const inputId = `${id}-${option.value}`;
                    return (
                        <label
                            key={option.value}
                            htmlFor={inputId}
                            className="inline-flex cursor-pointer items-center gap-1.5 text-[12px]"
                        >
                            <input
                                id={inputId}
                                type="checkbox"
                                className="size-3.5 accent-primary"
                                checked={value.includes(option.value)}
                                onChange={(event) => {
                                    onChange(
                                        event.target.checked
                                            ? [...value, option.value]
                                            : value.filter((v) => v !== option.value),
                                    );
                                }}
                            />
                            {option.label}
                        </label>
                    );
                })}
            </div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">
                Sin ninguna, el SKU se vende solo de contado.
            </p>
        </fieldset>
    );
}

export function FormSection({
    title,
    children,
    className,
}: {
    title: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <section aria-label={title} className={cn('flex flex-col gap-3', className)}>
            <div className={cn(SECTION, 'border-b border-border pb-1')}>{title}</div>
            {children}
        </section>
    );
}
